/**
 * Agent 协作系统
 * 实现多个 Agent 之间的协作模式和通信机制
 */

import { EventEmitter } from 'events';
import { v4 as uuidv4 } from 'uuid';
import { logger } from '../utils/logger';
import { AgentManager, Agent, AgentExecutionContext, AgentExecutionResult } from './agent-manager';
import { ExtendedTask } from '../types/extended-task';
import { Task, TaskStatus } from '../types';
import { TaskStore } from './task-store';
import { HookManager } from './hooks/hook-manager';
import { AgentAssignmentSystem, AgentRole } from './agent-assignment-system';

/**
 * 协作模式类型
 */
export type CollaborationMode = 
  | 'sequential'      // 顺序执行：一个接一个
  | 'parallel'        // 并行执行：同时进行
  | 'review-based'    // 审查模式：主执行者 -> 审查者
  | 'pair-programming' // 结对编程：两个 Agent 实时协作
  | 'hierarchical';   // 层级模式：主管 -> 执行者

/**
 * 协作任务定义
 */
export interface CollaborationTask {
  id: string;
  name: string;
  description: string;
  type: CollaborationMode;
  agents: CollaborationAgent[];
  context?: Record<string, any>;
  status: 'pending' | 'running' | 'completed' | 'failed';
  results?: CollaborationResult[];
  createdAt: Date;
  completedAt?: Date;
}

/**
 * 协作中的 Agent
 */
export interface CollaborationAgent {
  agentId: string;
  role: 'primary' | 'reviewer' | 'consultant' | 'supervisor' | 'worker';
  task: string;
  dependencies?: string[]; // 依赖的其他 Agent ID
  condition?: (previousResults: AgentExecutionResult[]) => boolean;
  required: boolean;
  order?: number; // 执行顺序（仅 sequential 模式）
}

/**
 * 协作上下文
 */
export interface CollaborationContext {
  task: ExtendedTask;
  mode: CollaborationMode;
  participants: AgentRole[];
  sharedMemory: Map<string, any>;
  messages: CollaborationMessage[];
  status: 'pending' | 'in-progress' | 'completed' | 'failed';
}

/**
 * 协作消息
 */
export interface CollaborationMessage {
  from: string;      // Agent ID
  to: string | '*';  // Agent ID 或 * 表示广播
  type: 'request' | 'response' | 'info' | 'error' | 'review';
  content: string;
  data?: any;
  timestamp: Date;
}

/**
 * 协作结果
 */
export interface CollaborationResult {
  taskId: string;
  mode: CollaborationMode;
  success: boolean;
  primaryResult: AgentExecutionResult;
  secondaryResults: AgentExecutionResult[];
  messages: CollaborationMessage[];
  recommendations?: string[];
  consensus?: {
    agreed: boolean;
    issues?: string[];
    resolution?: string;
  };
}

/**
 * 单个 Agent 的执行结果
 */
export interface AgentCollaborationResult {
  agentId: string;
  role: string;
  result: AgentExecutionResult;
  startedAt: Date;
  completedAt: Date;
}

/**
 * 协作策略
 */
export interface CollaborationStrategy {
  name: string;
  description: string;
  agents: {
    role: CollaborationAgent['role'];
    agentId: string;
    task: string;
  }[];
  flow: CollaborationMode;
}

/**
 * 协作协议接口
 */
export abstract class CollaborationProtocol {
  abstract name: string;
  abstract mode: CollaborationMode;
  
  abstract async execute(
    context: CollaborationContext,
    agentManager: AgentManager
  ): Promise<CollaborationResult>;
  
  protected broadcast(
    context: CollaborationContext, 
    from: string, 
    type: CollaborationMessage['type'],
    content: string,
    data?: any
  ): void {
    const message: CollaborationMessage = {
      from,
      to: '*',
      type,
      content,
      data,
      timestamp: new Date()
    };
    context.messages.push(message);
  }
  
  protected sendMessage(
    context: CollaborationContext,
    from: string,
    to: string,
    type: CollaborationMessage['type'],
    content: string,
    data?: any
  ): void {
    const message: CollaborationMessage = {
      from,
      to,
      type,
      content,
      data,
      timestamp: new Date()
    };
    context.messages.push(message);
  }
}

/**
 * 预定义的协作策略
 */
export const COLLABORATION_STRATEGIES: Record<string, CollaborationStrategy> = {
  'full-stack-development': {
    name: '全栈开发',
    description: '前后端协同开发',
    agents: [
      { role: 'primary', agentId: 'developer', task: '设计和实现核心功能' },
      { role: 'consultant', agentId: 'developer', task: '实现前端界面' },
      { role: 'reviewer', agentId: 'reviewer', task: '代码审查和质量保证' }
    ],
    flow: 'sequential'
  },
  'code-review': {
    name: '代码审查',
    description: '多角度代码审查',
    agents: [
      { role: 'primary', agentId: 'reviewer', task: '主要代码审查' },
      { role: 'consultant', agentId: 'developer', task: '技术建议' },
      { role: 'consultant', agentId: 'writer', task: '文档改进建议' }
    ],
    flow: 'parallel'
  },
  'feature-planning': {
    name: '功能规划',
    description: '产品功能设计和规划',
    agents: [
      { role: 'primary', agentId: 'developer', task: '技术可行性分析' },
      { role: 'reviewer', agentId: 'reviewer', task: '方案评审' },
      { role: 'consultant', agentId: 'writer', task: '文档编写' }
    ],
    flow: 'sequential'
  },
  'pair-programming': {
    name: '结对编程',
    description: '两个开发者协同编程',
    agents: [
      { role: 'primary', agentId: 'developer', task: '主要开发工作' },
      { role: 'consultant', agentId: 'developer', task: '代码审查和建议' }
    ],
    flow: 'pair-programming'
  },
  'hierarchical-development': {
    name: '层级开发',
    description: '主管分配任务给工作者',
    agents: [
      { role: 'supervisor', agentId: 'reviewer', task: '任务分解和分配' },
      { role: 'worker', agentId: 'developer', task: '实现具体功能' },
      { role: 'worker', agentId: 'writer', task: '编写文档' }
    ],
    flow: 'hierarchical'
  }
};

/**
 * 顺序执行协议
 */
export class SequentialProtocol extends CollaborationProtocol {
  name = 'sequential';
  mode: CollaborationMode = 'sequential';
  
  async execute(
    context: CollaborationContext,
    agentManager: AgentManager
  ): Promise<CollaborationResult> {
    const results: AgentExecutionResult[] = [];
    let previousResult: AgentExecutionResult | null = null;
    
    // 按顺序执行每个 Agent
    for (const participant of context.participants) {
      this.broadcast(
        context,
        'system',
        'info',
        `Starting execution with ${participant.agentId}`
      );
      
      const executionContext: AgentExecutionContext = {
        task: context.task,
        previousResults: previousResult ? [previousResult] : undefined,
        projectContext: {
          language: 'TypeScript',
          framework: 'React'
        }
      };
      
      try {
        const result = await agentManager.executeTask(
          participant.agentId,
          executionContext
        );
        
        results.push(result);
        previousResult = result;
        
        this.broadcast(
          context,
          participant.agentId,
          'response',
          'Execution completed',
          { success: result.success }
        );
        
        // 传递结果给下一个 Agent
        if (results.length < context.participants.length) {
          context.sharedMemory.set(`result_${participant.agentId}`, result);
        }
        
      } catch (error) {
        this.broadcast(
          context,
          participant.agentId,
          'error',
          `Execution failed: ${error}`
        );
        
        return {
          taskId: context.task.id,
          mode: this.mode,
          success: false,
          primaryResult: results[0] || this.createFailedResult(context.task.id, participant.agentId),
          secondaryResults: results.slice(1),
          messages: context.messages
        };
      }
    }
    
    return {
      taskId: context.task.id,
      mode: this.mode,
      success: true,
      primaryResult: results[0],
      secondaryResults: results.slice(1),
      messages: context.messages,
      recommendations: this.extractRecommendations(results)
    };
  }
  
  private extractRecommendations(results: AgentExecutionResult[]): string[] {
    const recommendations: string[] = [];
    
    results.forEach(result => {
      if (result.suggestions) {
        recommendations.push(...result.suggestions);
      }
    });
    
    return [...new Set(recommendations)]; // 去重
  }
  
  private createFailedResult(taskId: string, agentId: string): AgentExecutionResult {
    return {
      agentId,
      taskId,
      success: false,
      output: 'Execution failed',
      executionTime: 0
    };
  }
}

/**
 * 并行执行协议
 */
export class ParallelProtocol extends CollaborationProtocol {
  name = 'parallel';
  mode: CollaborationMode = 'parallel';
  
  async execute(
    context: CollaborationContext,
    agentManager: AgentManager
  ): Promise<CollaborationResult> {
    this.broadcast(
      context,
      'system',
      'info',
      `Starting parallel execution with ${context.participants.length} agents`
    );
    
    // 并行执行所有 Agent
    const executionPromises = context.participants.map(participant => {
      const executionContext: AgentExecutionContext = {
        task: context.task,
        projectContext: {
          language: 'TypeScript',
          framework: 'React'
        }
      };
      
      return agentManager
        .executeTask(participant.agentId, executionContext)
        .then((result: AgentExecutionResult) => {
          this.sendMessage(
            context,
            participant.agentId,
            '*',
            'response',
            'Execution completed',
            { success: result.success }
          );
          return result;
        })
        .catch((error: any) => {
          this.sendMessage(
            context,
            participant.agentId,
            '*',
            'error',
            `Execution failed: ${error}`
          );
          return this.createFailedResult(context.task.id, participant.agentId);
        });
    });
    
    const results = await Promise.all(executionPromises);
    
    // 分析结果一致性
    const consensus = this.analyzeConsensus(results);
    
    return {
      taskId: context.task.id,
      mode: this.mode,
      success: results.every(r => r.success),
      primaryResult: results[0],
      secondaryResults: results.slice(1),
      messages: context.messages,
      consensus,
      recommendations: this.mergeRecommendations(results)
    };
  }
  
  private analyzeConsensus(results: AgentExecutionResult[]): {
    agreed: boolean;
    issues?: string[];
    resolution?: string;
  } {
    // 简化的一致性分析
    const successCount = results.filter(r => r.success).length;
    const totalCount = results.length;
    
    if (successCount === totalCount) {
      return { agreed: true };
    }
    
    const issues = results
      .filter(r => !r.success)
      .map(r => `Agent ${r.agentId} failed`);
    
    return {
      agreed: false,
      issues,
      resolution: successCount > totalCount / 2 
        ? 'Majority succeeded, proceeding with consensus'
        : 'Majority failed, requires review'
    };
  }
  
  private mergeRecommendations(results: AgentExecutionResult[]): string[] {
    const allRecommendations = new Set<string>();
    
    results.forEach(result => {
      if (result.suggestions) {
        result.suggestions.forEach(s => allRecommendations.add(s));
      }
    });
    
    return Array.from(allRecommendations);
  }
  
  private createFailedResult(taskId: string, agentId: string): AgentExecutionResult {
    return {
      agentId,
      taskId,
      success: false,
      output: 'Execution failed',
      executionTime: 0
    };
  }
}

/**
 * 多 Agent 协作管理器
 */
export class AgentCollaborationManager extends EventEmitter {
  private collaborations = new Map<string, CollaborationTask>();
  private runningCollaborations = new Set<string>();
  private protocols: Map<CollaborationMode, CollaborationProtocol> = new Map();
  private activeCollaborations: Map<string, CollaborationContext> = new Map();
  private assignmentSystem: AgentAssignmentSystem;
  
  constructor(
    private agentManager: AgentManager,
    private taskStore?: TaskStore,
    private hookManager?: HookManager,
    workflowDir?: string
  ) {
    super();
    this.assignmentSystem = new AgentAssignmentSystem(workflowDir);
    
    // 注册默认协议
    this.registerProtocol(new SequentialProtocol());
    this.registerProtocol(new ParallelProtocol());
    this.registerProtocol(new ReviewBasedProtocol());
    this.registerProtocol(new PairProgrammingProtocol());
    this.registerProtocol(new HierarchicalProtocol());
  }
  
  /**
   * 初始化协作管理器
   */
  async initialize(): Promise<void> {
    await this.assignmentSystem.initialize();
  }
  
  /**
   * 注册协作协议
   */
  registerProtocol(protocol: CollaborationProtocol): void {
    this.protocols.set(protocol.mode, protocol);
    logger.info(`Registered collaboration protocol: ${protocol.name}`);
  }
  
  /**
   * 开始任务协作
   */
  async startCollaboration(
    task: ExtendedTask,
    mode: CollaborationMode = 'review-based',
    customParticipants?: AgentRole[]
  ): Promise<CollaborationResult> {
    const collaborationId = `${task.id}_${Date.now()}`;
    
    // 获取参与者
    let participants: AgentRole[];
    if (customParticipants) {
      participants = customParticipants;
    } else {
      // 使用分配系统自动分配
      const assignment = await this.assignmentSystem.assignAgentsToTask(task.id);
      participants = assignment.recommendations;
    }
    
    // 创建协作上下文
    const context: CollaborationContext = {
      task,
      mode,
      participants,
      sharedMemory: new Map(),
      messages: [],
      status: 'pending'
    };
    
    this.activeCollaborations.set(collaborationId, context);
    
    // 获取协议
    const protocol = this.protocols.get(mode);
    if (!protocol) {
      throw new Error(`Collaboration mode not supported: ${mode}`);
    }
    
    // 触发开始事件
    this.emit('collaboration:start', {
      collaborationId,
      task,
      mode,
      participants
    });
    
    context.status = 'in-progress';
    
    try {
      // 执行协作
      const result = await protocol.execute(context, this.agentManager);
      
      context.status = 'completed';
      
      // 触发完成事件
      this.emit('collaboration:complete', {
        collaborationId,
        result
      });
      
      // 更新 Agent 性能指标
      await this.updatePerformanceMetrics(result);
      
      // 触发 hook
      if (this.hookManager) {
        await this.hookManager.trigger('collaboration_completed', {
          task,
          mode,
          result
        });
      }
      
      return result;
      
    } catch (error) {
      context.status = 'failed';
      
      // 触发失败事件
      this.emit('collaboration:failed', {
        collaborationId,
        error
      });
      
      throw error;
      
    } finally {
      // 清理
      this.activeCollaborations.delete(collaborationId);
    }
  }
  
  /**
   * 创建协作任务
   */
  async createCollaboration(
    name: string,
    description: string,
    strategy: CollaborationStrategy | string,
    context?: Record<string, any>
  ): Promise<CollaborationTask> {
    // 如果是字符串，从预定义策略中获取
    if (typeof strategy === 'string') {
      const predefinedStrategy = COLLABORATION_STRATEGIES[strategy];
      if (!predefinedStrategy) {
        throw new Error(`Unknown collaboration strategy: ${strategy}`);
      }
      strategy = predefinedStrategy;
    }
    
    const collaboration: CollaborationTask = {
      id: uuidv4(),
      name,
      description,
      type: strategy.flow,
      agents: strategy.agents.map((agent, index) => ({
        agentId: agent.agentId,
        role: agent.role,
        task: agent.task,
        required: true,
        order: strategy.flow === 'sequential' ? index : undefined
      })),
      context,
      status: 'pending',
      createdAt: new Date()
    };
    
    this.collaborations.set(collaboration.id, collaboration);
    
    logger.info(`Created collaboration task: ${collaboration.name} (${collaboration.id})`);
    this.emit('collaborationCreated', collaboration);
    
    return collaboration;
  }
  
  /**
   * 获取活跃的协作
   */
  getActiveCollaborations(): CollaborationContext[] {
    return Array.from(this.activeCollaborations.values());
  }
  
  /**
   * 停止协作
   */
  async stopCollaboration(taskId: string): Promise<void> {
    const collaboration = Array.from(this.activeCollaborations.entries())
      .find(([id, ctx]) => ctx.task.id === taskId);
    
    if (collaboration) {
      const [collaborationId, context] = collaboration;
      context.status = 'failed';
      
      this.emit('collaboration:stopped', {
        collaborationId,
        taskId
      });
      
      this.activeCollaborations.delete(collaborationId);
    }
  }
  
  /**
   * 更新性能指标
   */
  private async updatePerformanceMetrics(result: CollaborationResult): Promise<void> {
    // 更新主执行者
    if (result.primaryResult) {
      await this.assignmentSystem.updateAgentPerformance(
        result.primaryResult.agentId,
        result.taskId,
        result.primaryResult.success,
        result.primaryResult.executionTime
      );
    }
    
    // 更新其他参与者
    for (const secondary of result.secondaryResults) {
      await this.assignmentSystem.updateAgentPerformance(
        secondary.agentId,
        result.taskId,
        secondary.success,
        secondary.executionTime
      );
    }
  }
  
  /**
   * 执行协作任务
   */
  async executeCollaboration(collaborationId: string): Promise<CollaborationResult> {
    const collaboration = this.collaborations.get(collaborationId);
    if (!collaboration) {
      throw new Error(`Collaboration not found: ${collaborationId}`);
    }
    
    if (!this.taskStore) {
      throw new Error('TaskStore is required for collaboration execution');
    }
    
    // 创建 ExtendedTask
    const task: ExtendedTask = {
      id: collaboration.id,
      content: collaboration.description,
      status: 'in_progress',
      priority: 'high',
      createdAt: collaboration.createdAt,
      updatedAt: new Date(),
      metadata: {
        source: 'collaboration',
        subtype: 'multi-agent'
      }
    };
    
    return this.startCollaboration(task, collaboration.type);
  }
  
  /**
   * 获取所有协作任务
   */
  getCollaborations(): CollaborationTask[] {
    return Array.from(this.collaborations.values());
  }
  
  /**
   * 获取协作任务状态
   */
  getCollaborationStatus(collaborationId: string): CollaborationTask | undefined {
    return this.collaborations.get(collaborationId);
  }
  
  /**
   * 汇总协作结果
   */
  summarizeResults(results: CollaborationResult[]): string {
    const summary: string[] = ['## 协作执行总结\n'];
    
    // 统计信息
    const totalAgents = results.length;
    const successfulAgents = results.filter(r => r.result.success).length;
    const totalTime = results.reduce((sum, r) => sum + r.result.executionTime, 0);
    
    summary.push(`- 参与 Agent: ${totalAgents}`);
    summary.push(`- 成功执行: ${successfulAgents}`);
    summary.push(`- 总耗时: ${(totalTime / 1000).toFixed(2)}秒\n`);
    
    // 各 Agent 结果
    summary.push('### Agent 执行结果\n');
    
    for (const result of results) {
      const agent = this.agentManager.getAgent(result.agentId);
      const status = result.result.success ? '✅' : '❌';
      
      summary.push(`#### ${status} ${agent?.name || result.agentId} (${result.role})`);
      summary.push(`- 任务: ${result.result.output.split('\n')[0]}`);
      summary.push(`- 耗时: ${(result.result.executionTime / 1000).toFixed(2)}秒`);
      
      if (result.result.artifacts && result.result.artifacts.length > 0) {
        summary.push(`- 产出: ${result.result.artifacts.length} 个文件`);
      }
      
      if (result.result.suggestions && result.result.suggestions.length > 0) {
        summary.push(`- 建议: ${result.result.suggestions.length} 条`);
      }
      
      summary.push('');
    }
    
    // 下一步建议
    const allNextSteps = results
      .filter(r => r.result.nextSteps)
      .flatMap(r => r.result.nextSteps!);
    
    if (allNextSteps.length > 0) {
      summary.push('### 建议的后续步骤\n');
      allNextSteps.forEach((step, index) => {
        summary.push(`${index + 1}. ${step}`);
      });
    }
    
    return summary.join('\n');
  }
  
  /**
   * 获取所有协作任务
   */
  getCollaborations(): CollaborationTask[] {
    return Array.from(this.collaborations.values());
  }
  
  /**
   * 获取协作任务状态
   */
  getCollaborationStatus(collaborationId: string): CollaborationTask | undefined {
    return this.collaborations.get(collaborationId);
  }
  
  /**
   * 创建自定义协作
   */
  async createCustomCollaboration(
    name: string,
    description: string,
    agents: CollaborationAgent[],
    type: 'sequential' | 'parallel' | 'conditional' = 'sequential',
    context?: Record<string, any>
  ): Promise<CollaborationTask> {
    const collaboration: CollaborationTask = {
      id: uuidv4(),
      name,
      description,
      type,
      agents,
      context,
      status: 'pending',
      createdAt: new Date()
    };
    
    this.collaborations.set(collaboration.id, collaboration);
    
    logger.info(`Created custom collaboration: ${name}`);
    this.emit('collaborationCreated', collaboration);
    
    return collaboration;
  }
  
  /**
   * 推荐协作策略
   */
  recommendStrategy(task: ExtendedTask): CollaborationStrategy | undefined {
    const taskContent = task.content.toLowerCase();
    
    // 根据任务内容推荐策略
    if (taskContent.includes('全栈') || taskContent.includes('前后端')) {
      return COLLABORATION_STRATEGIES['full-stack-development'];
    }
    
    if (taskContent.includes('审查') || taskContent.includes('review')) {
      return COLLABORATION_STRATEGIES['code-review'];
    }
    
    if (taskContent.includes('规划') || taskContent.includes('设计')) {
      return COLLABORATION_STRATEGIES['feature-planning'];
    }
    
    if (taskContent.includes('结对') || taskContent.includes('pair')) {
      return COLLABORATION_STRATEGIES['pair-programming'];
    }
    
    // 默认使用全栈开发策略
    return COLLABORATION_STRATEGIES['full-stack-development'];
  }
}

/**
 * 审查模式协议
 */
class ReviewBasedProtocol extends CollaborationProtocol {
  name = 'review-based';
  mode: CollaborationMode = 'review-based';
  
  async execute(
    context: CollaborationContext,
    agentManager: AgentManager
  ): Promise<CollaborationResult> {
    // 找到主执行者和审查者
    const primary = context.participants.find(p => p.role === 'primary');
    const reviewers = context.participants.filter(p => p.role === 'reviewer');
    
    if (!primary) {
      throw new Error('No primary agent assigned');
    }
    
    // 主执行者执行任务
    this.broadcast(context, 'system', 'info', `Primary agent ${primary.agentId} starting execution`);
    
    const primaryResult = await agentManager.executeTask(primary.agentId, {
      task: context.task,
      projectContext: { language: 'TypeScript', framework: 'React' }
    });
    
    // 审查者审查结果
    const reviewResults: AgentExecutionResult[] = [];
    
    for (const reviewer of reviewers) {
      this.sendMessage(
        context,
        'system',
        reviewer.agentId,
        'review',
        'Please review the primary execution',
        { primaryResult }
      );
      
      const reviewResult = await agentManager.executeTask(reviewer.agentId, {
        task: context.task,
        previousResults: [primaryResult],
        projectContext: { language: 'TypeScript', framework: 'React' }
      });
      
      reviewResults.push(reviewResult);
    }
    
    // 分析审查结果
    const allPassed = reviewResults.every(r => r.success);
    
    return {
      taskId: context.task.id,
      mode: this.mode,
      success: primaryResult.success && allPassed,
      primaryResult,
      secondaryResults: reviewResults,
      messages: context.messages,
      consensus: {
        agreed: allPassed,
        issues: reviewResults
          .filter(r => !r.success)
          .map(r => r.output)
      }
    };
  }
}

/**
 * 结对编程协议
 */
class PairProgrammingProtocol extends CollaborationProtocol {
  name = 'pair-programming';
  mode: CollaborationMode = 'pair-programming';
  
  async execute(
    context: CollaborationContext,
    agentManager: AgentManager
  ): Promise<CollaborationResult> {
    // 结对编程需要恰好两个 Agent
    if (context.participants.length !== 2) {
      throw new Error('Pair programming requires exactly 2 agents');
    }
    
    let [driver, navigator] = context.participants;
    
    // 模拟结对编程过程
    const rounds = 3; // 3轮交互
    const results: AgentExecutionResult[] = [];
    
    for (let round = 0; round < rounds; round++) {
      // Driver 编写代码
      this.sendMessage(
        context,
        'system',
        driver.agentId,
        'request',
        `Round ${round + 1}: Please write/update the code`
      );
      
      const driverResult = await agentManager.executeTask(driver.agentId, {
        task: context.task,
        previousResults: results,
        projectContext: { language: 'TypeScript', framework: 'React' }
      });
      
      results.push(driverResult);
      
      // Navigator 审查和建议
      this.sendMessage(
        context,
        driver.agentId,
        navigator.agentId,
        'response',
        'Code updated',
        { code: driverResult.output }
      );
      
      const navigatorResult = await agentManager.executeTask(navigator.agentId, {
        task: context.task,
        previousResults: [driverResult],
        projectContext: { language: 'TypeScript', framework: 'React' }
      });
      
      results.push(navigatorResult);
      
      // 交换角色
      [driver, navigator] = [navigator, driver];
    }
    
    return {
      taskId: context.task.id,
      mode: this.mode,
      success: results.every(r => r.success),
      primaryResult: results[0],
      secondaryResults: results.slice(1),
      messages: context.messages,
      recommendations: this.extractAllSuggestions(results)
    };
  }
  
  private extractAllSuggestions(results: AgentExecutionResult[]): string[] {
    const suggestions = new Set<string>();
    
    results.forEach(result => {
      if (result.suggestions) {
        result.suggestions.forEach(s => suggestions.add(s));
      }
    });
    
    return Array.from(suggestions);
  }
}

/**
 * 层级协作协议
 */
class HierarchicalProtocol extends CollaborationProtocol {
  name = 'hierarchical';
  mode: CollaborationMode = 'hierarchical';
  
  async execute(
    context: CollaborationContext,
    agentManager: AgentManager
  ): Promise<CollaborationResult> {
    // 找到主管和执行者
    const supervisor = context.participants.find(p => p.role === 'reviewer');
    const workers = context.participants.filter(p => p.role === 'primary' || p.role === 'consultant');
    
    if (!supervisor || workers.length === 0) {
      throw new Error('Hierarchical mode requires a supervisor and at least one worker');
    }
    
    // 主管分解任务
    this.broadcast(
      context,
      'system',
      'info',
      `Supervisor ${supervisor.agentId} analyzing and delegating tasks`
    );
    
    const supervisorPlan = await agentManager.executeTask(supervisor.agentId, {
      task: context.task,
      projectContext: { language: 'TypeScript', framework: 'React' }
    });
    
    // 工作者执行子任务
    const workerResults: AgentExecutionResult[] = [];
    
    for (const worker of workers) {
      this.sendMessage(
        context,
        supervisor.agentId,
        worker.agentId,
        'request',
        'Please execute your assigned subtask',
        { plan: supervisorPlan.output }
      );
      
      const workerResult = await agentManager.executeTask(worker.agentId, {
        task: context.task,
        previousResults: [supervisorPlan],
        projectContext: { language: 'TypeScript', framework: 'React' }
      });
      
      workerResults.push(workerResult);
    }
    
    // 主管整合结果
    const integrationResult = await agentManager.executeTask(supervisor.agentId, {
      task: {
        ...context.task,
        content: `Integrate results from ${workers.length} workers`
      },
      previousResults: workerResults,
      projectContext: { language: 'TypeScript', framework: 'React' }
    });
    
    return {
      taskId: context.task.id,
      mode: this.mode,
      success: integrationResult.success,
      primaryResult: integrationResult,
      secondaryResults: [supervisorPlan, ...workerResults],
      messages: context.messages,
      recommendations: this.consolidateRecommendations([supervisorPlan, ...workerResults, integrationResult])
    };
  }
  
  private consolidateRecommendations(results: AgentExecutionResult[]): string[] {
    const recommendations = new Map<string, number>();
    
    // 统计每个建议出现的次数
    results.forEach(result => {
      if (result.suggestions) {
        result.suggestions.forEach(suggestion => {
          recommendations.set(suggestion, (recommendations.get(suggestion) || 0) + 1);
        });
      }
    });
    
    // 按出现次数排序
    return Array.from(recommendations.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([suggestion]) => suggestion);
  }
}